import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import BrainLoading from "PFComponents/brain_loading/brain_loading";
import { Button } from "PFComponents/button";
import { FileUpload } from "PFComponents/file_upload";
import { Typography } from "PFComponents/typography";
import { usePusherEvent } from "PFCore/base/pusher";
import { gidToId } from "PFCore/helpers/gid";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Timeout } from "PFTypes/timeout";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./cv_upload.module.scss";

type CVUploadProps = {
  processingMessage: string;
  handleSuccess: (response: any) => void;
  handleTimeout: VoidFunction;
  handleCancel?: VoidFunction;
};

export const CVUpload = ({
  processingMessage,
  handleSuccess,
  handleTimeout,
  handleCancel
}: CVUploadProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "common.cvUpload" });
  const uploadInputRef = useRef(null);
  const timeoutRef = useRef<Timeout>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUnmounted, setIsUnmounted] = useState(false);

  const handleCVUpToDate = useCallback((data) => {
    if (data.status === "up-to-date") {
      handleSuccess?.(data);
    }
  }, []);
  const handleNewChangeRequest = useCallback((data) => {
    data.id = gidToId(data.resource_gid);
    handleSuccess?.(data);
  }, []);

  usePusherEvent(`private-profile-${currentProfile.id}`, "cv", handleCVUpToDate);
  usePusherEvent(`private-profile-${currentProfile.id}`, "change_request_create", handleNewChangeRequest);

  useEffect(
    () => () => {
      setIsUnmounted(true);
      timeoutRef.current && clearTimeout(timeoutRef.current);
    },
    []
  );

  const notifyTimeout = () => {
    if (isUnmounted) {
      return;
    }

    handleTimeout && handleTimeout();

    showGrowl({
      message: t("cvStillProcessing")
    });
  };

  const handleChange = () => {
    setIsLoading(true);
    setIsProcessing(false);
  };

  const handleLoad = () => {
    showGrowl({ message: t("upload.success") });
    setIsLoading(false);
    setIsProcessing(true);
    timeoutRef.current = setTimeout(notifyTimeout, 30000);
  };

  const handleError = (response) => {
    let message = t("upload.error");

    if (response && response.errors && response.errors.length > 0) {
      const error = response.errors[0];

      if (error.detail && error.detail.indexOf("not a valid CV file") !== -1) {
        message = t("upload.notValidExt");
      } else {
        message = error.detail;
      }
    }

    showGrowl({ message, kind: "error" });
    setIsLoading(false);
    setIsProcessing(false);
  };

  return (
    <div className={css.root}>
      {(isLoading || isProcessing) && <BrainLoading style={{ margin: 0 }} />}
      {(isLoading || isProcessing) && (
        <Typography variant="h1" className={css.title}>
          {t("title")}
        </Typography>
      )}

      {isLoading && <Typography variant="bodyRegular">{t("uploading")}</Typography>}
      {isProcessing && <Typography variant="bodyRegular">{processingMessage}</Typography>}

      {!isLoading && !isProcessing && (
        <>
          <FileUpload
            inputRef={uploadInputRef}
            url="curriculums"
            name="file"
            qaId="profile-edit-curriculum-input"
            onChange={handleChange}
            onLoad={handleLoad}
            onError={handleError}
            header={t("title")}
            description={
              <div className={css.attachment}>
                <Typography variant="bodyRegular">{t("uploadInfo")}</Typography>
                <Typography variant="bodyRegular">{t("extensionsInfo")}</Typography>
              </div>
            }
          />
          {handleCancel && (
            <div className={css.skipButton}>
              <Button kind="text" onClick={handleCancel} qaId="profile-edit-skip">
                {t("orSkip")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
